import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { useDataSource, type DataSourceId } from '../contexts/DataSourceContext';
import { useAttributionOptional } from '../contexts/AttributionContext';
import { InstallConsentSummary } from '../components/install-consent/InstallConsentSummary';
import { INSTALL_CONSENT_VERSIONS } from '../components/install-consent/install-consent-copy';
import {
  WELCOME_SPLASH_COPY,
  WELCOME_USER_AS_ACTOR_COPY,
  getWelcomeSplashVisibleCopy,
  lintWelcomeTrustCopy,
} from '../lib/welcome/copy-governance';
import {
  WELCOME_SOURCE_CATALOG,
  WELCOME_SOURCE_SKIP_CONSEQUENCE_COPY,
  WELCOME_SOURCE_SKIP_NO_GRANT_COPY,
  WELCOME_SOURCE_SKIP_REASON,
  isWelcomeSourceId,
  type WelcomeSourceDescriptor,
} from '../lib/welcome/source-catalog';
import { createConservativeWelcomeRecoveryStatusAdapter } from '../lib/welcome/recovery-status';
import {
  createWelcomeOutcomeRecord,
  writeWelcomeOutcome,
  type WelcomeOutcomeState,
} from '../lib/welcome/outcomes';
import {
  createWelcomeResumeIntent,
  writeWelcomeResumeIntent,
} from '../lib/welcome/resume-intent';
import { runWelcomeFirstSync, type WelcomeFirstSyncState } from '../lib/welcome/first-sync';
import {
  evaluateWelcomeMissionReadiness,
  type WelcomeMissionReadiness,
  type WelcomeMissionReadinessKind,
} from '../lib/welcome/mission-readiness';
import {
  getSlice08SandboxIdentity,
  isSlice08SandboxOAuthReturn,
  writeSlice08SandboxWelcomeSourceEvidence,
} from '../lib/qa/slice08-sandbox';

type WelcomeStep = 'splash' | 'identity' | 'sources' | 'source-skip';
type IdentityAttemptState = 'idle' | 'requested' | 'cancelled' | 'failed';
type SourceReturnState = 'idle' | 'connecting' | 'connected' | 'failed';
type FirstSyncRenderState = 'idle' | 'syncing' | WelcomeFirstSyncState;
type InstallConsentRenderState = 'idle' | 'reviewing' | 'recording' | 'recorded' | 'skipped' | 'error';

export interface WelcomeProps {
  entryRoute?: 'welcome' | 'onboarding';
}

const WELCOME_LAST_OUTCOME_STORAGE_KEY = 'ownyou_welcome_last_outcome';

function sanitizeWelcomeReason(value: string | null | undefined): string | null {
  if (!value) return null;
  const clean = value.replace(/[^a-zA-Z0-9_.:-]/g, '_').slice(0, 80);
  return clean || null;
}

function outcomeStateForMissionReadiness(
  readinessKind: WelcomeMissionReadinessKind,
  firstSyncState: WelcomeFirstSyncState,
): WelcomeOutcomeState {
  if (readinessKind === 'ready') return 'ready';
  if (readinessKind === 'building') return 'building';
  if (readinessKind === 'user_skipped_sources') return 'user_skipped_sources';
  if (firstSyncState === 'blocked_timeout') return 'timed_out';
  return 'blocked';
}

export function Welcome({ entryRoute = 'welcome' }: WelcomeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, isAuthenticated, isLoading: authLoading, error: authError, connect } = useAuth();
  const { store, isReady: storeReady } = useStore();
  const { connectSource, syncSource } = useDataSource();
  const attribution = useAttributionOptional();
  const [step, setStep] = useState<WelcomeStep>('splash');
  const [isConnectingIdentity, setIsConnectingIdentity] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [identityAttempt, setIdentityAttempt] = useState<IdentityAttemptState>('idle');
  const [installConsentState, setInstallConsentState] = useState<InstallConsentRenderState>('idle');
  const [installConsentError, setInstallConsentError] = useState<string | null>(null);
  const [sourceReturnState, setSourceReturnState] = useState<SourceReturnState>('idle');
  const [sourceReturnId, setSourceReturnId] = useState<DataSourceId | null>(null);
  const [sourceReturnReason, setSourceReturnReason] = useState<string | null>(null);
  const [firstSyncState, setFirstSyncState] = useState<FirstSyncRenderState>('idle');
  const [firstSyncReason, setFirstSyncReason] = useState<string | null>(null);
  const [missionReadiness, setMissionReadiness] = useState<WelcomeMissionReadiness | null>(null);

  const sandboxIdentity = getSlice08SandboxIdentity();
  const walletId = wallet?.address ?? sandboxIdentity?.walletAddress ?? 'anonymous';
  const identityReady = Boolean((isAuthenticated && wallet && !authLoading) || sandboxIdentity);
  const routeId = entryRoute === 'onboarding' ? 'onboarding-alias' : 'welcome';
  const recoveryAdapter = useMemo(() => createConservativeWelcomeRecoveryStatusAdapter(), []);
  const recovery = useMemo(
    () => recoveryAdapter.read({
      evidenceReadable: true,
      hasCurrentDeviceIdentity: identityReady,
    }),
    [identityReady, recoveryAdapter],
  );

  const trustLint = lintWelcomeTrustCopy(getWelcomeSplashVisibleCopy());
  const identityState = useMemo(() => {
    if (identityReady) return 'ready';
    if (authLoading || isConnectingIdentity) return 'loading';
    if (authError || identityError || identityAttempt === 'failed') return 'failed';
    if (identityAttempt === 'cancelled') return 'cancelled';
    return 'unavailable';
  }, [authError, authLoading, identityAttempt, identityError, identityReady, isConnectingIdentity]);

  const recordWelcomeOutcome = useCallback(async (input: {
    state: WelcomeOutcomeState;
    reasonCode: string;
    sourceId?: DataSourceId | null;
    nextRoute?: string;
    evidenceRefs?: readonly string[];
  }) => {
    const common = {
      walletId,
      userId: walletId,
      state: input.state,
      reasonCode: input.reasonCode,
      ...(input.sourceId ? { sourceId: input.sourceId } : {}),
      routeId,
      ...(input.nextRoute ? { nextRoute: input.nextRoute } : {}),
      ...(input.evidenceRefs ? { evidenceRefs: input.evidenceRefs } : {}),
    };

    try {
      const record = store && storeReady
        ? await writeWelcomeOutcome({ store, ...common })
        : createWelcomeOutcomeRecord(common);
      sessionStorage.setItem(WELCOME_LAST_OUTCOME_STORAGE_KEY, JSON.stringify(record));
    } catch (error) {
      console.warn('[Welcome] Failed to record Welcome outcome:', error);
    }
  }, [routeId, store, storeReady, walletId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') !== 'oauth-return') return;

    const rawSourceId = params.get('source');
    const sourceId = rawSourceId && isWelcomeSourceId(rawSourceId) ? rawSourceId : null;
    const status = params.get('status');
    const reason = sanitizeWelcomeReason(params.get('reason'));
    setStep('sources');
    setSourceReturnId(sourceId);
    setSourceReturnReason(reason);

    if (status !== 'success' || !sourceId) {
      setSourceReturnState('failed');
      void recordWelcomeOutcome({
        state: status === 'denied' ? 'denied' : 'failed',
        reasonCode: reason ?? (status === 'denied' ? 'oauth-denied' : 'oauth-return-failed'),
        sourceId,
        evidenceRefs: ['welcome:oauth-return'],
      });
      return;
    }

    if (!storeReady) {
      setSourceReturnState('connecting');
      setSourceReturnReason('store-not-ready');
      return;
    }

    void (async () => {
      if (isSlice08SandboxOAuthReturn(reason)) {
        if (!store) {
          setSourceReturnState('failed');
          setSourceReturnReason('store-not-ready');
          await recordWelcomeOutcome({
            state: 'failed',
            reasonCode: 'store-not-ready',
            sourceId,
            evidenceRefs: ['welcome:qa-source-evidence'],
          });
          return;
        }

        setSourceReturnState('connecting');
        try {
          await writeSlice08SandboxWelcomeSourceEvidence({ store, sourceId });
          setSourceReturnState('connected');
          setSourceReturnReason('source-connected');
          setFirstSyncState('building_missions');
          setFirstSyncReason('source-write-observed-building-missions');
          const readiness = evaluateWelcomeMissionReadiness({
            sourceId,
            firstSyncState: 'building_missions',
            firstSyncReason: 'source-write-observed-building-missions',
          });
          setMissionReadiness(readiness);
          await recordWelcomeOutcome({
            state: outcomeStateForMissionReadiness(readiness.kind, 'building_missions'),
            reasonCode: readiness.reasonCode,
            sourceId,
            nextRoute: readiness.nextRoute,
            evidenceRefs: [
              'welcome:qa-source-evidence',
              `welcome:mission-readiness:${readiness.kind}`,
            ],
          });
          if (readiness.nextRoute) {
            navigate(readiness.nextRoute);
          }
        } catch (error) {
          const reasonCode = sanitizeWelcomeReason(error instanceof Error ? error.message : null) ?? 'qa-source-evidence-failed';
          setSourceReturnState('failed');
          setSourceReturnReason(reasonCode);
          await recordWelcomeOutcome({
            state: 'failed',
            reasonCode,
            sourceId,
            evidenceRefs: ['welcome:qa-source-evidence'],
          });
        }
        return;
      }

      const tokenDataRaw = sessionStorage.getItem('oauth_token_data');
      const pendingProvider = sessionStorage.getItem('oauth_provider') as DataSourceId | null;
      if (!tokenDataRaw || pendingProvider !== sourceId) {
        setSourceReturnState('failed');
        setSourceReturnReason('missing-token-handoff');
        await recordWelcomeOutcome({
          state: 'failed',
          reasonCode: 'missing-token-handoff',
          sourceId,
          evidenceRefs: ['welcome:oauth-token-handoff'],
        });
        return;
      }

      sessionStorage.removeItem('oauth_token_data');
      sessionStorage.removeItem('oauth_provider');
      setSourceReturnState('connecting');

      try {
        const tokenData = JSON.parse(tokenDataRaw);
        await connectSource(sourceId, tokenData);
        setSourceReturnState('connected');
        setSourceReturnReason('source-connected');
        setFirstSyncState('syncing');
        const outcome = await runWelcomeFirstSync({ sourceId, syncSource });
        setFirstSyncState(outcome.state);
        setFirstSyncReason(outcome.reasonCode);
        const readiness = evaluateWelcomeMissionReadiness({
          sourceId,
          firstSyncState: outcome.state,
          firstSyncReason: outcome.reasonCode,
        });
        setMissionReadiness(readiness);
        await recordWelcomeOutcome({
          state: outcomeStateForMissionReadiness(readiness.kind, outcome.state),
          reasonCode: readiness.reasonCode,
          sourceId,
          nextRoute: readiness.nextRoute,
          evidenceRefs: [
            `welcome:first-sync:${outcome.state}`,
            `welcome:mission-readiness:${readiness.kind}`,
          ],
        });
        if (readiness.nextRoute) {
          navigate(readiness.nextRoute);
        }
      } catch (error) {
        const reasonCode = sanitizeWelcomeReason(error instanceof Error ? error.message : null) ?? 'source-connect-failed';
        setSourceReturnState('failed');
        setSourceReturnReason(reasonCode);
        await recordWelcomeOutcome({
          state: 'failed',
          reasonCode,
          sourceId,
          evidenceRefs: ['welcome:source-connect'],
        });
      }
    })();
  }, [connectSource, location.search, navigate, recordWelcomeOutcome, store, storeReady, syncSource]);

  const goToIdentityOrSources = useCallback(() => {
    setStep(identityReady ? 'sources' : 'identity');
  }, [identityReady]);

  const handleConnectIdentity = useCallback(async () => {
    setIdentityError(null);
    setIdentityAttempt('requested');
    setIsConnectingIdentity(true);
    try {
      await connect();
    } catch (error) {
      setIdentityAttempt('failed');
      setIdentityError(error instanceof Error ? error.message : 'Identity connection failed.');
    } finally {
      setIsConnectingIdentity(false);
    }
  }, [connect]);

  const handleCancelIdentity = useCallback(() => {
    setIdentityAttempt('cancelled');
    setIsConnectingIdentity(false);
  }, []);

  const handleStartSource = useCallback((source: WelcomeSourceDescriptor) => {
    if (!identityReady || source.availability !== 'available') return;
    writeWelcomeResumeIntent(createWelcomeResumeIntent({
      sourceId: source.id,
      routeId: entryRoute === 'onboarding' ? 'onboarding' : 'welcome',
    }));
    navigate(`/settings?tab=data&source=${source.id}&from=welcome`);
  }, [entryRoute, identityReady, navigate]);

  const handleRecordInstallConsent = useCallback(async () => {
    if (!attribution || installConsentState === 'recording') return;
    setInstallConsentError(null);
    setInstallConsentState('recording');
    try {
      const result = await attribution.recordInstallConsent({
        protocol_version: INSTALL_CONSENT_VERSIONS.protocol_version,
        disclosure_policy_version: INSTALL_CONSENT_VERSIONS.disclosure_policy_version,
        trust_registry_version: INSTALL_CONSENT_VERSIONS.trust_registry_version,
      });
      if (result.ok) {
        setInstallConsentState('recorded');
        return;
      }
      setInstallConsentState('error');
      setInstallConsentError(result.error);
    } catch (error) {
      setInstallConsentState('error');
      setInstallConsentError(error instanceof Error ? error.message : 'install-consent-record-failed');
    }
  }, [attribution, installConsentState]);

  const handleConfirmSourceSkip = useCallback(async () => {
    const nextRoute = '/connections?welcome=user_skipped_sources';
    await recordWelcomeOutcome({
      state: 'user_skipped_sources' as const,
      reasonCode: WELCOME_SOURCE_SKIP_REASON,
      nextRoute,
      evidenceRefs: ['welcome:source-skip'],
    });
    navigate(nextRoute);
  }, [navigate, recordWelcomeOutcome]);

  return (
    <main
      data-testid="welcome-route"
      data-entry-route={entryRoute}
      className="min-h-[calc(100vh-4rem)] bg-[#f7f5f1] px-4 py-6 text-slate-950 sm:px-8"
    >
      <section className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <header className="flex flex-col gap-2">
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-500">{WELCOME_SPLASH_COPY.eyebrow}</p>
          <h1 className="text-3xl font-semibold tracking-normal sm:text-4xl">{WELCOME_SPLASH_COPY.title}</h1>
        </header>

        {step === 'splash' && (
          <section
            data-testid="welcome-splash"
            data-copy-lint-ok={trustLint.ok ? 'true' : 'false'}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-xl font-semibold">You stay in control</h2>
                <p className="mt-3 max-w-2xl text-base leading-7 text-slate-700">{WELCOME_USER_AS_ACTOR_COPY}</p>
                <p className="mt-2 max-w-2xl text-base leading-7 text-slate-700">{WELCOME_SPLASH_COPY.body}</p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="welcome-recovery-status">
                <p className="text-sm font-semibold text-slate-800">{recovery.title}</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{recovery.description}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="welcome-primary-action"
                  onClick={goToIdentityOrSources}
                  className="rounded-md bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {WELCOME_SPLASH_COPY.primaryAction}
                </button>
                <button
                  type="button"
                  data-testid="welcome-view-connections"
                  onClick={() => navigate('/connections')}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  View connections
                </button>
              </div>
            </div>
          </section>
        )}

        {step === 'identity' && (
          <section
            data-testid="welcome-identity-step"
            data-identity-state={identityState}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 className="text-xl font-semibold">Identity readiness</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-700">
              Connect your wallet identity before choosing sources, so source outcomes can be tied to your account without exposing source content.
            </p>
            {identityState === 'cancelled' && (
              <p className="mt-3 text-sm font-medium text-amber-700">
                Identity setup was cancelled. Source setup stays locked until identity is ready.
              </p>
            )}
            {identityState === 'ready' && (
              <p className="mt-3 text-sm font-medium text-emerald-700">
                Identity ready for wallet {walletId.slice(0, 6)}...{walletId.slice(-4)}.
              </p>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {!identityReady && (
                <button
                  type="button"
                  data-testid="welcome-identity-connect"
                  onClick={handleConnectIdentity}
                  disabled={authLoading || isConnectingIdentity}
                  className={cn(
                    'rounded-md px-4 py-2.5 text-sm font-semibold text-white',
                    authLoading || isConnectingIdentity ? 'bg-slate-400' : 'bg-slate-950 hover:bg-slate-800',
                  )}
                >
                  {authLoading || isConnectingIdentity ? 'Connecting...' : 'Connect identity'}
                </button>
              )}
              {!identityReady && identityAttempt === 'requested' && (
                <button
                  type="button"
                  data-testid="welcome-identity-cancel"
                  onClick={handleCancelIdentity}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Not now
                </button>
              )}
              {identityReady && (
                <button
                  type="button"
                  data-testid="welcome-identity-continue"
                  onClick={() => setStep('sources')}
                  className="rounded-md bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Choose sources
                </button>
              )}
              {(identityError || authError) && <p className="text-sm text-red-600">{identityError ?? authError}</p>}
            </div>
          </section>
        )}

        {step === 'sources' && (
          <section data-testid="welcome-source-step" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-semibold">Choose a source</h2>
              <p className="max-w-2xl text-base leading-7 text-slate-700">
                Missions need real source evidence. Start with one connected source, or skip for now and review Connections.
              </p>
              <p className="max-w-2xl text-sm leading-6 text-slate-600">
                {WELCOME_SOURCE_SKIP_CONSEQUENCE_COPY} {WELCOME_SOURCE_SKIP_NO_GRANT_COPY}
              </p>
            </div>

            {sourceReturnState !== 'idle' && (
              <div
                data-testid="welcome-source-return-status"
                data-state={sourceReturnState}
                data-source-id={sourceReturnId ?? ''}
                className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <p className="text-sm font-semibold text-slate-800">
                  {sourceReturnState === 'connected'
                    ? 'Source connected'
                    : sourceReturnState === 'connecting'
                      ? 'Finishing source connection'
                      : 'Source connection needs attention'}
                </p>
                {sourceReturnReason && (
                  <p className="mt-1 text-sm leading-6 text-slate-600">{sourceReturnReason}</p>
                )}
              </div>
            )}

            {firstSyncState !== 'idle' && (
              <div
                data-testid="welcome-first-sync-status"
                data-state={firstSyncState}
                data-source-id={sourceReturnId ?? ''}
                className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <p className="text-sm font-semibold text-slate-800">
                  {firstSyncState === 'syncing'
                    ? 'Running first sync'
                    : firstSyncState === 'building_missions'
                      ? 'Building missions'
                      : firstSyncState === 'sync_success' || firstSyncState === 'zero_item_success'
                        ? 'First sync complete'
                        : 'First sync needs attention'}
                </p>
                {firstSyncReason && (
                  <p className="mt-1 text-sm leading-6 text-slate-600">{firstSyncReason}</p>
                )}
              </div>
            )}

            {missionReadiness && !missionReadiness.nextRoute && (
              <div
                data-testid="welcome-mission-readiness-status"
                data-state={missionReadiness.kind}
                data-grant-eligible={missionReadiness.grantEligible ? 'true' : 'false'}
                className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <p className="text-sm font-semibold text-slate-800">Mission readiness blocked</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">{missionReadiness.reasonCode}</p>
              </div>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {WELCOME_SOURCE_CATALOG.map(source => {
                const unavailable = source.availability !== 'available';
                const actionDisabled = unavailable || !identityReady;
                return (
                  <article
                    key={source.id}
                    data-testid={`welcome-source-${source.id}`}
                    data-source-id={source.id}
                    data-source-availability={source.availability}
                    className={cn(
                      'rounded-md border p-4',
                      unavailable ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-200 bg-white text-slate-950',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold">{source.label}</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600">{source.permissionText}</p>
                        {source.availabilityReason && (
                          <p className="mt-2 text-sm font-medium text-slate-500">{source.availabilityReason}</p>
                        )}
                      </div>
                      <span className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium capitalize">
                        {source.type}
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid={`welcome-source-connect-${source.id}`}
                      onClick={() => handleStartSource(source)}
                      disabled={actionDisabled}
                      className={cn(
                        'mt-4 w-full rounded-md px-3 py-2 text-sm font-semibold',
                        actionDisabled
                          ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                          : 'bg-slate-950 text-white hover:bg-slate-800',
                      )}
                    >
                      {unavailable ? 'Unavailable' : `Connect ${source.label}`}
                    </button>
                  </article>
                );
              })}
            </div>

            <div
              data-testid="welcome-install-consent-optional"
              data-state={installConsentState}
              data-provider-mounted={attribution ? 'true' : 'false'}
              className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4"
            >
              <div className="flex flex-col gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Optional install consent</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                    You can record protocol consent now, or skip it and keep setting up sources. This does not decide mission readiness and never routes you to Wallet.
                  </p>
                  {attribution?.installConsentStatus === 'recorded' && (
                    <p className="mt-2 text-sm font-medium text-emerald-700">Install consent is already recorded.</p>
                  )}
                </div>

                {(installConsentState === 'reviewing'
                  || installConsentState === 'recording'
                  || installConsentState === 'recorded'
                  || installConsentState === 'error') && (
                  <div className="rounded-md border border-slate-200 bg-white p-4">
                    <InstallConsentSummary />
                  </div>
                )}

                {installConsentError && (
                  <p
                    data-testid="welcome-install-consent-error"
                    className="text-sm font-medium text-red-700"
                    role="alert"
                  >
                    {installConsentError}
                  </p>
                )}

                <div className="flex flex-wrap gap-3">
                  {installConsentState === 'idle' || installConsentState === 'skipped' ? (
                    <button
                      type="button"
                      data-testid="welcome-install-consent-review"
                      onClick={() => setInstallConsentState('reviewing')}
                      className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      Review install consent
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="welcome-install-consent-record"
                      onClick={handleRecordInstallConsent}
                      disabled={!attribution || installConsentState === 'recording' || installConsentState === 'recorded'}
                      className={cn(
                        'rounded-md px-4 py-2.5 text-sm font-semibold',
                        !attribution || installConsentState === 'recording' || installConsentState === 'recorded'
                          ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                          : 'bg-slate-950 text-white hover:bg-slate-800',
                      )}
                    >
                      {installConsentState === 'recording'
                        ? 'Recording...'
                        : installConsentState === 'recorded'
                          ? 'Recorded'
                          : 'Record consent'}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="welcome-install-consent-skip"
                    onClick={() => {
                      setInstallConsentError(null);
                      setInstallConsentState('skipped');
                    }}
                    className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Skip consent for now
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-200 pt-4">
              <button
                type="button"
                data-testid="welcome-source-skip"
                onClick={() => setStep('source-skip')}
                className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                  Skip source setup
              </button>
            </div>
          </section>
        )}

        {step === 'source-skip' && (
          <section data-testid="welcome-source-skip-step" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-semibold">Continue without a source</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-slate-700">
              {WELCOME_SOURCE_SKIP_CONSEQUENCE_COPY}
            </p>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              {WELCOME_SOURCE_SKIP_NO_GRANT_COPY}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="welcome-source-skip-confirm"
                onClick={handleConfirmSourceSkip}
                className="rounded-md bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Go to Connections
              </button>
              <button
                type="button"
                data-testid="welcome-source-skip-cancel"
                onClick={() => setStep('sources')}
                className="rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Choose a source
              </button>
            </div>
          </section>
        )}
      </section>
    </main>
  );
}

export default Welcome;
